/**
 * Mutu Ancak PDF Preview Component
 * - Desktop: iframe viewer (full browser PDF features)
 * - Mobile/WebView: canvas rendering of all pages (Android compatible) + download button
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Button,
  HStack,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import { MutuAncakData, PenaltyRate } from '../types';
import { generateMutuAncakPDF, groupDataBySupervisor } from '../utils/pdfGenerator';
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { format } from 'date-fns';
import { trackDownload } from '../../../activity';

/** Detect if running on a desktop browser (not mobile/tablet/WebView) */
const isDesktop = (): boolean => {
  if (globalThis.window === undefined) return false;
  const ua = navigator.userAgent;
  // Detect Android WebView
  if (/wv/.test(ua) || /Android.*Version\/[\d.]+.*Chrome\/[\d.]+/.test(ua)) return false;
  // Detect mobile / tablet
  if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return false;
  return true;
};

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

interface MutuAncakPdfPreviewProps {
  data: MutuAncakData[];
  inspectionDate: string;
  penaltyRates?: PenaltyRate[];
  isLoading?: boolean;
}

// ─────────────────────────────────────────────
// Sub-component: renders a single PDF page to canvas
// ─────────────────────────────────────────────
const PdfPageCanvas: React.FC<{ pdfDoc: PDFDocumentProxy; pageNumber: number }> = ({
  pdfDoc,
  pageNumber,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<any>(null);

  useEffect(() => {
    const render = async () => {
      try {
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
          renderTaskRef.current = null;
        }
        const page = await pdfDoc.getPage(pageNumber);
        const canvas = canvasRef.current;
        if (!canvas) return;

        const containerWidth = canvas.parentElement?.clientWidth || window.innerWidth;
        const viewport = page.getViewport({ scale: 1 });
        const scale = containerWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale: Math.max(scale, 1.5) });

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const renderTask = page.render({ canvasContext: ctx, viewport: scaledViewport });
        renderTaskRef.current = renderTask;
        await renderTask.promise;
        renderTaskRef.current = null;
      } catch (err: any) {
        if (err?.name !== 'RenderingCancelledException') {
          console.error('Error rendering page', pageNumber, err);
        }
      }
    };
    render();
  }, [pdfDoc, pageNumber]);

  return (
    <canvas
      ref={canvasRef}
      style={{ display: 'block', width: '100%', height: 'auto' }}
    />
  );
};

// ─────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────
export const MutuAncakPdfPreview: React.FC<MutuAncakPdfPreviewProps> = ({
  data,
  inspectionDate,
  penaltyRates,
  isLoading = false,
}) => {
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfArrayBuffer, setPdfArrayBuffer] = useState<ArrayBuffer | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [renderError, setRenderError] = useState(false);
  const desktop = isDesktop();
  const toast = useToast();

  // Generate PDF once whenever data/filters change
  useEffect(() => {
    const generatePreview = async () => {
      if (!data || data.length === 0) {
        setPdfDoc(null);
        setPdfBlobUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        setPdfArrayBuffer(null);
        setTotalPages(0);
        return;
      }

      try {
        setGenerating(true);
        setRenderError(false);

        const groupedData = groupDataBySupervisor(data);
        const doc = generateMutuAncakPDF(groupedData, inspectionDate, penaltyRates);
        const buffer = doc.output('arraybuffer');
        setPdfArrayBuffer(buffer);

        if (desktop) {
          // Desktop: create blob URL for iframe
          const blob = new Blob([buffer], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          setPdfBlobUrl(prev => {
            if (prev) URL.revokeObjectURL(prev);
            return url;
          });
        } else {
          // Mobile: load via pdfjs for canvas rendering
          const loadingTask = pdfjsLib.getDocument({ data: buffer.slice(0) });
          const pdf = await loadingTask.promise;
          setPdfDoc(pdf);
          setTotalPages(pdf.numPages);
        }
      } catch (error) {
        console.error('Error generating PDF preview:', error);
        setRenderError(true);
        toast({
          title: 'Error',
          description: 'Gagal membuat preview PDF',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setGenerating(false);
      }
    };

    generatePreview();

    return () => {
      setPdfBlobUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, inspectionDate, penaltyRates]);

  // Download handler for mobile
  const handleDownload = useCallback(() => {
    if (!pdfArrayBuffer) return;
    try {
      const blob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Mutu_Ancak_${format(new Date(inspectionDate), 'yyyy-MM-dd')}.pdf`;
      trackDownload('Form Mutu Ancak', { inspection_date: inspectionDate });
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast({
        title: 'Berhasil',
        description: 'PDF berhasil diunduh',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Gagal mengunduh PDF',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [pdfArrayBuffer, inspectionDate, toast]);

  // ── Loading state ──────────────────────────
  if (isLoading || generating) {
    return (
      <Box
        w="full"
        h="500px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        bg="gray.50"
      >
        <VStack spacing={4}>
          <Spinner size="lg" color="customGreen.500" />
          <Text color="gray.600">Generating PDF preview...</Text>
        </VStack>
      </Box>
    );
  }

  // ── No data / error state ──────────────────
  const hasContent = desktop ? !!pdfBlobUrl : !!pdfDoc;
  if (!hasContent || renderError) {
    return (
      <Box
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="1px dashed"
        borderColor="gray.300"
        borderRadius="md"
        bg="gray.50"
        p={6}
        minH="400px"
      >
        <VStack spacing={4}>
          <Text color="gray.500" fontSize="sm" fontWeight="medium">
            {renderError ? 'Gagal memuat preview PDF' : 'No data available to preview'}
          </Text>
          <Text color="gray.400" fontSize="xs">
            {renderError ? 'Preview PDF tidak dapat ditampilkan di perangkat ini' : 'Please apply filters and load data first'}
          </Text>
          {renderError && pdfArrayBuffer && (
            <Button
              leftIcon={<DownloadIcon />}
              colorScheme="green"
              variant="solid"
              size="sm"
              onClick={handleDownload}
            >
              Download PDF
            </Button>
          )}
        </VStack>
      </Box>
    );
  }

  // ── Desktop: iframe viewer ─────────────────
  if (desktop) {
    return (
      <Box
        w="full"
        borderRadius="md"
        overflow="hidden"
        border="1px solid"
        borderColor="gray.200"
        bg="white"
        boxShadow="sm"
      >
        <iframe
          title="PDF Preview"
          src={pdfBlobUrl!}
          style={{ width: '100%', height: '700px', border: 'none' }}
        />
      </Box>
    );
  }

  // ── Mobile/WebView: all pages canvas + download ─
  return (
    <VStack w="full" spacing={3} align="stretch">
      {/* Download button */}
      <HStack justify="flex-end">
        <Button
          leftIcon={<DownloadIcon />}
          size="sm"
          colorScheme="green"
          variant="solid"
          onClick={handleDownload}
          isDisabled={!pdfArrayBuffer}
        >
          Download PDF
        </Button>
      </HStack>

      {/* All pages rendered as stacked canvases */}
      <VStack
        w="full"
        spacing={2}
        align="stretch"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        bg="gray.100"
        p={2}
      >
        {pdfDoc && Array.from({ length: totalPages }, (_, i) => (
          <Box
            key={i + 1}
            bg="white"
            boxShadow="sm"
            borderRadius="sm"
            overflow="hidden"
          >
            <PdfPageCanvas pdfDoc={pdfDoc} pageNumber={i + 1} />
          </Box>
        ))}
      </VStack>

      {totalPages > 0 && (
        <Text fontSize="xs" color="gray.400" textAlign="center">
          {totalPages} halaman
        </Text>
      )}
    </VStack>
  );
};

export default MutuAncakPdfPreview;